import React, { FC } from 'react'
import { RouteComponentProps, matchPath, withRouter } from 'react-router-dom'
import LoadingPage from 'components/LoadingPage'
import {
  versionedRouteKeys,
  VersionedRouteKeys,
  getRouteTemplate,
  getAllTemplatesForVersionedRoute,
  RouteKeys,
} from 'constants/routes'
import { IS_EMBEDDED } from 'constants/embedding'

export interface RouteBasedLoadingPageProps extends RouteComponentProps {}

const isVersionedRouteKey = (
  routeKey: RouteKeys
): routeKey is VersionedRouteKeys => {
  return versionedRouteKeys.includes(routeKey as VersionedRouteKeys)
}

const isMatching = (
  pathname: string,
  routeKey: RouteKeys,
  exact = true
): boolean => {
  const routePaths = isVersionedRouteKey(routeKey)
    ? getAllTemplatesForVersionedRoute(routeKey)
    : [getRouteTemplate(routeKey)]

  const match = matchPath(pathname, {
    path: routePaths,
    exact,
  })

  return !!match
}

const RouteBasedLoadingPage: FC<RouteBasedLoadingPageProps> = ({
  location,
}) => {
  // We want to maintain full control over what is rendered while embedded
  // therefore return null early here, rather than attempting to return
  // a dynamic loading state
  if (IS_EMBEDDED) return null

  const match = (routeKey: RouteKeys, exact = true) =>
    isMatching(location.pathname, routeKey, exact)

  switch (true) {
    case match('PROTOTYPE'):
      return <LoadingPage.Prototype />
    case match('ARTBOARD_DETAIL'):
    case match('ARTBOARD_DETAIL_UUID'):
    case match('ARTBOARD_REVISION'):
      return <LoadingPage.Artboard />
    case match('SHARE_VIEW'):
      return <LoadingPage.Sidebar />
    case match('SHARE_PAGE_CANVAS_VIEW'):
      return <LoadingPage.Sidebar sidebarRight={null} />
    case match('SSO_LINK_ACCOUNT'):
    case match('SSO_TOS'):
      return <LoadingPage.Sso />
    case match('WORKSPACE_SUBSCRIBE'):
    case match('WORKSPACE_CREATE'):
    case match('WORKSPACE_CREATE_DONE'):
    case match('WORKSPACE_INVITE'):
      return <LoadingPage.WorkspaceSubscribe />
    case match('SIGN_UP'):
    case match('SIGN_IN'):
    case match('FORGOT_PASSWORD'):
    case match('RESET_PASSWORD'):
      return <LoadingPage.IndexLayout variant="teal" />
    case match('UPDATES'):
    case match('PERSONAL_SETTINGS'):
    case match('WORKSPACE', false):
      return <LoadingPage.WorkspaceSidebar />
    default:
      return <LoadingPage />
  }
}

export default withRouter(RouteBasedLoadingPage)
